import { graphql, readInlineData } from 'relay-runtime';
import { Environment } from 'react-relay';
import { initialTracking } from './checkoutTrackingActions';
import { addressFormUpdated } from './checkoutShippingActions';
import { updateStorage } from '../helpers/checkoutStorage';
import { transactionSetupAction } from './checkoutUIActions';
import {
    checkoutSetUpActions_item$key,
    checkoutSetUpActions_item$data,
} from './__generated__/checkoutSetUpActions_item.graphql';
import {
    checkoutSetUpActionsSetUpPanes_buyer$key,
    checkoutSetUpActionsSetUpPanes_buyer$data,
} from './__generated__/checkoutSetUpActionsSetUpPanes_buyer.graphql';
import {
    checkoutSetUpActionsSetUpPanes_preCheckoutTransaction$key,
    checkoutSetUpActionsSetUpPanes_preCheckoutTransaction$data,
} from './__generated__/checkoutSetUpActionsSetUpPanes_preCheckoutTransaction.graphql';
import { checkoutSetUpActions_transaction$key } from './__generated__/checkoutSetUpActions_transaction.graphql';
import { checkoutSetUpActions_viewer$key } from './__generated__/checkoutSetUpActions_viewer.graphql';
import { checkoutSetUpActions_buyer$key } from './__generated__/checkoutSetUpActions_buyer.graphql';
import { createAction } from '@reduxjs/toolkit';
import { AppDispatch } from '../reducers/root';
import { Pane } from '../constants/checkoutConstants';

function getItemInfo(item: checkoutSetUpActions_item$data): {
    id: string;
    image: string | null | undefined;
    isPrivateVisibilityType: boolean;
    title: string | null;
} {
    const { id, isPrivateVisibilityType, serviceId, title, photos } = item;
    const versions = photos?.[0]?.versions || [];
    const version = versions.find(v => v?.type?.name === 'thumb');
    return {
        id: serviceId || id,
        image: version?.webPath,
        isPrivateVisibilityType: !!isPrivateVisibilityType,
        title,
    };
}

export const setUpPanesAction = createAction<
    (payload: {
        buyer: checkoutSetUpActionsSetUpPanes_buyer$key | null;
        deliveryOptions: {
            serviceId: string | null;
            isLocalPickup: boolean | null;
        }[];
        preCheckoutTransaction: checkoutSetUpActionsSetUpPanes_preCheckoutTransaction$key | null;
    }) => {
        payload: {
            buyer: checkoutSetUpActionsSetUpPanes_buyer$data | null;
            deliveryOptions: {
                serviceId: string | null;
                isLocalPickup: boolean | null;
            }[];
            preCheckoutTransaction: checkoutSetUpActionsSetUpPanes_preCheckoutTransaction$data | null;
        };
    }
>(
    'CHECKOUT_PANES_SETUP',
    ({ buyer: buyerRef, preCheckoutTransaction: preCheckoutTransactionRef, ...payload }) => {
        const buyer = readInlineData(
            graphql`
                fragment checkoutSetUpActionsSetUpPanes_buyer on User @inline {
                    ...checkoutPaneReducer_buyer
                }
            `,
            buyerRef
        );

        const preCheckoutTransaction = readInlineData(
            graphql`
                fragment checkoutSetUpActionsSetUpPanes_preCheckoutTransaction on Transaction
                @inline {
                    ...checkoutPaneReducer_preCheckoutTransaction
                }
            `,
            preCheckoutTransactionRef
        );
        return { payload: { buyer, preCheckoutTransaction, ...payload } };
    }
);

export function setUpCheckout(
    payload: {
        item: checkoutSetUpActions_item$key;
        buyer: checkoutSetUpActions_buyer$key | null;
        transaction: checkoutSetUpActions_transaction$key;
        viewer: checkoutSetUpActions_viewer$key;
        isMobile: boolean;
        environment: Environment;
        checkoutPageViewTracking: Record<string, unknown>; // passed to `initialTracking`
        initialPane: Pane | null;
        country: string;
        isSelectedAddressValid: boolean;
    },
    dispatch: AppDispatch
): void {
    const {
        item: itemRef,
        buyer: buyerRef,
        transaction: transactionRef,
        viewer: viewerRef,
        country,
        isSelectedAddressValid,
    } = payload;
    const item = readInlineData(
        graphql`
            fragment checkoutSetUpActions_item on Item @inline {
                ...checkoutTrackingActions_item
                id
                isPrivateVisibilityType
                serviceId
                title
                photos {
                    versions {
                        type {
                            name
                        }
                        webPath
                    }
                }
            }
        `,
        itemRef
    );
    const transaction = readInlineData(
        graphql`
            fragment checkoutSetUpActions_transaction on Transaction @inline {
                ...checkoutUIActionsTransactionSetup_transaction
                ...checkoutTrackingActions_transaction
                isPrivateOffer
                isPreAuthNegotiation
            }
        `,
        transactionRef
    );

    const { isPrivateOffer, isPreAuthNegotiation } = transaction;

    const buyer = readInlineData(
        graphql`
            fragment checkoutSetUpActions_buyer on User @inline {
                ...checkoutUIActionsTransactionSetup_buyer
                ...checkoutTrackingActions_buyer
                isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
                profile {
                    email
                }
            }
        `,
        buyerRef
    );

    const viewer = readInlineData(
        graphql`
            fragment checkoutSetUpActions_viewer on Viewer @inline {
                ...checkoutUIActionsTransactionSetup_viewer
            }
        `,
        viewerRef
    );

    updateStorage({
        itemId: item?.serviceId,
        itemInfo: getItemInfo(item),
        isPrivateOffer,
        isPreAuthNegotiation,
    });

    dispatch(
        transactionSetupAction({
            viewer,
            buyer,
            transaction,
        })
    );

    const userEmail = buyer?.profile?.email;
    if (userEmail) {
        dispatch(
            addressFormUpdated({
                field: 'email',
                value: userEmail,
                isAdding: false,
            })
        );
    }

    const trackingPayload = {
        ...payload,
        itemRef: item,
        buyerRef: buyer,
        transactionRef: transaction,
        country,
        isSelectedAddressValid,
    };
    dispatch(initialTracking(trackingPayload));
}
